import { api } from './api'
import { env } from '../config/env'

export interface SemanticSearchResult {
  id: string
  sessionId: number
  active: boolean
  approved: boolean
  credits: string
  description: string
  endDate: string
  eventId: number
  eventTypeCode: string | null
  eventTypeDisplay: string | null
  eventTypeEnabled: boolean
  eventTypeId: number
  eventTypeSort: number
  fee: number
  limit: number
  locationId: number
  notOnline: boolean
  numOnWaitingList: number
  numRegistered: number
  onlineTypeId: number | null
  organizationId: number
  regEnd: string | null
  regStart: string | null
  roomName: string
  searchField: string
  searchResult: string
  siteId: number
  siteName: string | null
  startDate: string
  subtitle: string | null
  title: string
  showInCatalog: boolean | null
  score: number
  embedding: string | null
  embeddingsGenerated: boolean
}

export interface SemanticSearchResponse {
  message?: string
  text?: string
  response?: string
  answer?: string
  sessions?: SemanticSearchResult[]
  results?: SemanticSearchResult[]
}

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

export interface StreamChunk {
  content: string
  done: boolean
}

export const semanticSearchService = {
  search: async (query: string, chatHistory: ChatMessage[], limit: number = 5): Promise<SemanticSearchResponse> => {
    try {
      const response = await api.post<SemanticSearchResponse | string>(
        `/session/semantic-search?limit=${limit}`,
        {
          query,
          chatHistory
        }
      )
      
      // Handle both response formats: object with message/sessions or just string
      if (typeof response === 'string') {
        return { message: response, text: response }
      }
      if (Array.isArray(response)) {
        return { sessions: response, results: response }
      }
      return response as SemanticSearchResponse
    } catch (error) {
      throw error
    }
  },

  // New streaming search method
  searchStream: async (
    query: string, 
    chatHistory: ChatMessage[], 
    limit: number = 10,
    onChunk: (chunk: string) => void,
    onComplete: () => void,
    onError: (error: Error) => void
  ): Promise<void> => {
    try {
      // Get the base URL - handle both development and production
      const isDev = import.meta.env.DEV
      const baseURL = isDev ? '/api' : (env('VITE_API_BASE_URL') || '')
      const tenant = env('VITE_TENANT_ID')
      const fullURL = `${baseURL}/session/${tenant}/semantic-search-stream?limit=${limit}`
      
      const token = localStorage.getItem('accessToken')
      
      const response = await fetch(fullURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          query,
          chatHistory
        })
      })

      if (!response.ok) {
        // Try to get error details from response
        const errorText = await response.text()
        throw new Error(`HTTP error! status: ${response.status}, details: ${errorText}`)
      }

      const reader = response.body?.getReader()
      const decoder = new TextDecoder()

      if (!reader) {
        throw new Error('Response body is not readable')
      }

      let buffer = ''

      for (;;) {
        const { done, value } = await reader.read()

        if (done) {
          // Process any remaining buffer as final JSON array
          if (buffer.trim()) {
            try {
              const jsonArray = JSON.parse(buffer)
              if (Array.isArray(jsonArray)) {
                // Process any remaining chunks in the array
                for (const chunk of jsonArray) {
                  if (typeof chunk === 'string' && chunk.trim()) {
                    onChunk(chunk)
                  }
                }
              }
            } catch (e) {
              console.error('Error parsing search response chunk:', e)
            }
          }
          
          onComplete()
          break
        }

        // Decode the chunk
        const decoded = decoder.decode(value, { stream: true })
        buffer += decoded

        // Try to parse as JSON array (the API returns a complete JSON array)
        try {
          const jsonArray = JSON.parse(buffer)
          if (Array.isArray(jsonArray)) {
            // Process each chunk in the array
            for (const chunk of jsonArray) {
              if (typeof chunk === 'string' && chunk.trim()) {
                onChunk(chunk)
                
                // Add delay between chunks for smooth streaming effect
                // 40ms creates a nice, readable pace
                await new Promise(resolve => setTimeout(resolve, 40))
              }
            }
            
            // Clear buffer since we've processed the complete array
            buffer = ''
            
            // Break out since we've received the complete response
            onComplete()
            break
          }
        } catch (e) {
          // Not a complete JSON array yet, continue reading
          // This is expected when the response is still being received
        }
      }
    } catch (error) {
      onError(error as Error)
    }
  },
}
